import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { GBText } from '@greenbackapp/ui';
import { spacingTokens } from '../../config/designTokens';
import { focusRing } from '../../styles/mixins';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
  testId?: string;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  'aria-label'?: string;
  testId?: string;
  maxItems?: number;
  itemsBeforeCollapse?: number;
  itemsAfterCollapse?: number;
}

const BreadcrumbNav = styled.nav`
  display: flex;
  align-items: center;
`;

const BreadcrumbList = styled.ol`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  flex-wrap: wrap;
`;

const BreadcrumbListItem = styled.li`
  display: flex;
  align-items: center;
`;

const Separator = styled.span`
  margin: 0 ${spacingTokens.sm};
  user-select: none;
`;

const StyledAnchor = styled.a`
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    ${focusRing()}
  }
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    ${focusRing()}
  }
`;

const EllipsisButton = styled.button`
  background: none;
  border: none;
  padding: 0 ${spacingTokens.xs};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    ${focusRing()}
  }
`;

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  separator = "/",
  'aria-label': ariaLabel = "Breadcrumb",
  testId = "breadcrumb",
  maxItems = 4,
  itemsBeforeCollapse = 1,
  itemsAfterCollapse = 1,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!items || items.length === 0) return null;

  const renderItem = (item: BreadcrumbItem, index: number, isLast: boolean) => {
    return (
      <BreadcrumbListItem key={index}>
        {index > 0 && (
          <Separator aria-hidden="true">
            <GBText variant="labelSmall" color="secondary">{separator}</GBText>
          </Separator>
        )}

        {isLast ? (
          <GBText
            variant="labelSmall"
            color="primary"
            bold
            // @ts-ignore
            aria-current="page"
            data-testid={`${testId}-current`}
          >
            {item.label}
          </GBText>
        ) : item.href ? (
          <StyledAnchor
            href={item.href}
            onClick={(e) => {
              e.preventDefault();
              navigate(item.href!);
            }}
            data-testid={`${testId}-link-${index}`}
          >
            <GBText variant="labelSmall" color="link">
              {item.label}
            </GBText>
          </StyledAnchor>
        ) : item.onClick ? (
          <StyledButton onClick={item.onClick} type="button" data-testid={`${testId}-button-${index}`}>
            <GBText variant="labelSmall" color="link">
              {item.label}
            </GBText>
          </StyledButton>
        ) : (
          <GBText variant="labelSmall" color="secondary" data-testid={`${testId}-text-${index}`}>
            {item.label}
          </GBText>
        )}
      </BreadcrumbListItem>
    );
  };

  if (items.length <= maxItems || isExpanded) {
    return (
      <BreadcrumbNav aria-label={ariaLabel} data-testid={testId}>
        <BreadcrumbList>
          {items.map((item, index) => renderItem(item, index, index === items.length - 1))}
        </BreadcrumbList>
      </BreadcrumbNav>
    );
  }

  // Collapse logic
  const beforeItems = items.slice(0, itemsBeforeCollapse);
  const afterItems = items.slice(items.length - itemsAfterCollapse);
  const collapsedCount = items.length - beforeItems.length - afterItems.length;

  if (collapsedCount <= 0) {
    return (
      <BreadcrumbNav aria-label={ariaLabel} data-testid={testId}>
        <BreadcrumbList>
          {items.map((item, index) => renderItem(item, index, index === items.length - 1))}
        </BreadcrumbList>
      </BreadcrumbNav>
    );
  }

  return (
    <BreadcrumbNav aria-label={ariaLabel} data-testid={testId}>
      <BreadcrumbList>
        {beforeItems.map((item, index) => renderItem(item, index, false))}

        <BreadcrumbListItem key="ellipsis">
          <Separator aria-hidden="true">
            <GBText variant="labelSmall" color="secondary">{separator}</GBText>
          </Separator>
          <EllipsisButton
            onClick={() => setIsExpanded(true)}
            aria-label="Show all breadcrumbs"
            title="Show all"
            data-testid={`${testId}-ellipsis`}
          >
            <GBText variant="labelSmall" color="secondary">...</GBText>
          </EllipsisButton>
        </BreadcrumbListItem>

        {afterItems.map((item, index) => {
          const originalIndex = items.length - itemsAfterCollapse + index;
          return (
            <React.Fragment key={originalIndex}>
              {renderItem(item, originalIndex, originalIndex === items.length - 1)}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </BreadcrumbNav>
  );
};
